// src/pages/ChangePinPage.tsx — Slice D commit 5.
//
// Route: /me/pin. Major divergence from docs/prototype/apps2.jsx lines
// 545-585: the prototype uses four-digit <Field type="password"> inputs;
// the real NYMR PIN is a six-character [KFWT][0-9][KFWT][0-9][0-9][0-9]
// string driven by the PinDots + NumPad primitives (matching the format
// the Login screen uses).
//
// Three sequential steps — current PIN, then new PIN, then confirm. The
// final step calls POST /api/auth/change-pin which validates the current
// PIN, hashes the new one, clears the session cookie server-side, and
// audits `auth.pin.change`. On success we render a sage "PIN changed"
// card and redirect to /login after 1.6s so the user signs back in with
// the new PIN.
//
// SSO sessions are bounced to /me — change-PIN is PIN-only.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, apiFetch } from "../api";
import { useCurrentUser } from "../auth";
import { C, F } from "../theme";
import {
  Card,
  Icons,
  NumPad,
  PinDots,
  PIN_LENGTH,
  PIN_REGEX,
  TopBar,
} from "../components/ui";

type Step = "current" | "new" | "confirm" | "submitting" | "done";

export function ChangePinPage() {
  const navigate = useNavigate();
  const { me, loading } = useCurrentUser();

  const [step, setStep] = useState<Step>("current");
  const [currentBuffer, setCurrentBuffer] = useState("");
  const [newBuffer, setNewBuffer] = useState("");
  const [confirmBuffer, setConfirmBuffer] = useState("");
  const [shake, setShake] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Guard — bounce SSO to /me. Use a useEffect so the redirect happens
  // post-render (avoid setState-in-render warnings).
  useEffect(() => {
    if (!loading && me && me.authMethod === "sso") {
      navigate("/me", { replace: true });
    }
  }, [loading, me, navigate]);

  // Done-state redirect — wait 1.6s then route to /login (the server has
  // already cleared the session cookie, so any apiFetch from here would
  // 401 anyway).
  useEffect(() => {
    if (step !== "done") return;
    const t = window.setTimeout(() => navigate("/login", { replace: true }), 1600);
    return () => window.clearTimeout(t);
  }, [step, navigate]);

  // Active buffer + setter for the current step.
  function activeBuffer(): string {
    if (step === "new") return newBuffer;
    if (step === "confirm") return confirmBuffer;
    return currentBuffer; // "current" | "submitting" use currentBuffer for display only during in-flight
  }
  function setActiveBuffer(value: string) {
    if (step === "new") setNewBuffer(value);
    else if (step === "confirm") setConfirmBuffer(value);
    else setCurrentBuffer(value);
  }

  function triggerShake() {
    setShake(true);
    window.setTimeout(() => setShake(false), 420);
  }

  function handlePress(char: string) {
    if (step === "submitting" || step === "done") return;
    const b = activeBuffer();
    if (b.length >= PIN_LENGTH) return;
    setErr(null);
    setActiveBuffer(b + char);
  }

  function handleDelete() {
    if (step === "submitting" || step === "done") return;
    const b = activeBuffer();
    if (b.length === 0) return;
    setErr(null);
    setActiveBuffer(b.slice(0, -1));
  }

  async function handleSubmit() {
    if (step === "submitting" || step === "done") return;

    if (step === "current") {
      if (!PIN_REGEX.test(currentBuffer)) {
        triggerShake();
        setCurrentBuffer("");
        setErr("PIN format is L-N-L-N-N-N (letters K F W T).");
        return;
      }
      setErr(null);
      setStep("new");
      return;
    }

    if (step === "new") {
      if (!PIN_REGEX.test(newBuffer)) {
        triggerShake();
        setNewBuffer("");
        setErr("PIN format is L-N-L-N-N-N (letters K F W T).");
        return;
      }
      setErr(null);
      setStep("confirm");
      return;
    }

    if (step === "confirm") {
      if (confirmBuffer !== newBuffer) {
        triggerShake();
        setConfirmBuffer("");
        setErr("PINs don't match. Try the confirmation again.");
        return;
      }

      // All three buffers ready — POST.
      setErr(null);
      setStep("submitting");
      try {
        await apiFetch("/api/auth/change-pin", {
          method: "POST",
          body: JSON.stringify({
            currentPin: currentBuffer,
            newPin: newBuffer,
          }),
        });
        setStep("done");
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          setErr("Current PIN incorrect.");
          triggerShake();
          setCurrentBuffer("");
          setNewBuffer("");
          setConfirmBuffer("");
          setStep("current");
        } else if (e instanceof ApiError && e.status === 400) {
          setErr(e.message);
          triggerShake();
          // 400 is most often new-PIN format — clear that buffer.
          setNewBuffer("");
          setConfirmBuffer("");
          setStep("new");
        } else {
          setErr(e instanceof Error ? e.message : "Change PIN failed");
          setStep("confirm");
        }
      }
    }
  }

  if (loading || !me) {
    return (
      <div>
        <TopBar title="Change my PIN" onBack={() => navigate("/me")} />
      </div>
    );
  }

  if (me.authMethod === "sso") {
    return (
      <div>
        <TopBar title="Change my PIN" onBack={() => navigate("/me")} />
      </div>
    );
  }

  if (step === "done") {
    return (
      <div>
        <TopBar title="Change PIN" onBack={() => navigate("/me")} />
        <div style={{ padding: "40px 16px", textAlign: "center" }}>
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: 18,
              background: C.sageBg,
              border: `1px solid ${C.sageLt}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <Icons.Check s={36} c={C.sageDk} />
          </div>
          <h2
            style={{
              fontFamily: F.serif,
              fontSize: 24,
              fontWeight: 700,
              color: C.ink,
              margin: 0,
              letterSpacing: -0.4,
            }}
          >
            PIN changed
          </h2>
          <p
            style={{
              fontFamily: F.serif,
              fontStyle: "italic",
              fontSize: 15,
              color: C.inkSoft,
              margin: "6px 0 0",
              fontWeight: 500,
            }}
          >
            Logging you out — use your new PIN to sign back in.
          </p>
        </div>
      </div>
    );
  }

  const prompt =
    step === "new"
      ? "Enter new PIN"
      : step === "confirm"
      ? "Confirm new PIN"
      : "Enter current PIN";
  const subline =
    step === "new"
      ? "Pick a fresh six-character PIN."
      : step === "confirm"
      ? "Type the new PIN once more to confirm."
      : "We need your current PIN to make a change.";

  return (
    <div>
      <TopBar title="Change my PIN" onBack={() => navigate("/me")} />
      <div style={{ padding: "14px 16px 24px" }}>
        <Card>
          <div
            style={{
              fontFamily: F.serif,
              fontSize: 18,
              fontWeight: 700,
              color: C.ink,
              letterSpacing: -0.2,
              textAlign: "center",
              marginBottom: 4,
            }}
          >
            {prompt}
          </div>
          <div
            style={{
              fontFamily: F.serif,
              fontStyle: "italic",
              fontSize: 13,
              color: C.inkSoft,
              fontWeight: 500,
              textAlign: "center",
              marginBottom: 14,
            }}
          >
            {subline}
          </div>

          <PinDots value={activeBuffer()} shake={shake} />

          {err && (
            <div
              style={{
                padding: "10px 14px",
                background: C.signalBg,
                border: `1px solid rgba(184,58,43,0.22)`,
                borderRadius: 12,
                fontFamily: F.body,
                fontSize: 13,
                fontWeight: 700,
                color: C.signalDk,
                margin: "0 0 14px",
              }}
              role="status"
            >
              {err}
            </div>
          )}

          <NumPad
            length={activeBuffer().length}
            onPress={handlePress}
            onDelete={handleDelete}
            onSubmit={handleSubmit}
            disabled={step === "submitting"}
          />

          <ProgressDots step={step} />
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Three-step progress dots
// ---------------------------------------------------------------------------

function ProgressDots({ step }: { step: Step }) {
  const stepIndex =
    step === "current" ? 0 : step === "new" ? 1 : step === "confirm" || step === "submitting" ? 2 : 2;
  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        justifyContent: "center",
        marginTop: 18,
      }}
      aria-hidden="true"
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            width: i === stepIndex ? 22 : 6,
            height: 6,
            borderRadius: 4,
            background: i <= stepIndex ? C.brassDk : C.hairline,
            transition: "all 0.15s",
          }}
        />
      ))}
    </div>
  );
}
